import React from 'react';
import { usePlayer } from './PlayerContext';
import { formatTime } from './format';
import './player.css';

/**
 * Sticky mini player bar (T2). Rendered once in App.js above the tab
 * content, so it is visible on every tab whenever an episode is loaded.
 */
function PlayerBar() {
  const { currentlyPlaying, isPlaying, currentTime, duration, togglePause, seek } = usePlayer();

  if (!currentlyPlaying) return null;

  const subtitle = currentlyPlaying.sources?.length
    ? `Sources: ${currentlyPlaying.sources.join(', ')}`
    : currentlyPlaying.scheduleName || '';

  const durationLabel = duration > 0 ? formatTime(duration) : currentlyPlaying.duration;

  return (
    <div className="player-bar">
      <div className="player-bar__row">
        <button
          type="button"
          className="player-bar__toggle"
          aria-label={isPlaying ? 'Pause' : 'Play'}
          onClick={togglePause}
        >
          {isPlaying ? '⏸️' : '▶️'}
        </button>
        <div className="player-bar__info">
          <div className="player-bar__title">{currentlyPlaying.title}</div>
          <div className="player-bar__meta">
            {isPlaying ? 'Playing' : 'Paused'}
            {subtitle ? ` • ${subtitle}` : ''}
          </div>
        </div>
        <div className="player-bar__time">
          {formatTime(currentTime)} / {durationLabel}
        </div>
      </div>
      <div
        className="player-scrubber"
        role="slider"
        tabIndex={0}
        aria-label="Seek"
        aria-valuemin={0}
        aria-valuemax={duration > 0 ? Math.round(duration) : 0}
        aria-valuenow={Math.round(currentTime)}
        aria-valuetext={`${formatTime(currentTime)} of ${durationLabel}`}
        onClick={(e) => {
          if (duration > 0) {
            const rect = e.currentTarget.getBoundingClientRect();
            seek((e.clientX - rect.left) / rect.width);
          }
        }}
        onKeyDown={(e) => {
          if (duration <= 0) return;
          if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            e.preventDefault();
            seek((currentTime + 5) / duration);
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            e.preventDefault();
            seek((currentTime - 5) / duration);
          } else if (e.key === 'Home') {
            e.preventDefault();
            seek(0);
          } else if (e.key === 'End') {
            e.preventDefault();
            seek(1);
          }
        }}
      >
        {/* width is playback-position-driven — must stay a dynamic style */}
        <div
          className="player-scrubber__fill"
          style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
        ></div>
      </div>
    </div>
  );
}

export default PlayerBar;
